import { useMemo } from 'react';

import { usePlansCatalog, ExplorePlanOffer } from 'app/hooks/useSubscriptionPlans/usePlansCatalog';
import { getNewTierPlans, NewTierPlan } from 'app/hooks/useSubscriptionPlans/getNewTierPlans';
import type { PricingCatalogErrorCode } from 'app/services/pricingCatalog';

import { getComparePlanPill } from './getComparePlanPill';
import { formatFeatureValue } from './formatFeatureValue';
import { COMPARE_PLANS_TABLE_ROWS } from './comparePlansTableData';
import type { ComparePlansColumn, ComparePlansTableRow } from './types';

// Derives the 6c table from each offer's `features` array (same list used,
// filtered, by the 6a summary). COMPARE_PLANS_TABLE_ROWS becomes the flag-OFF fallback: the
// hardcoded catalog has no per-feature availability data. Lower paid tiers still come in the
// offers payload (`can_subscribe: false`); `tierKeys` always carries all 3 columns so a tier
// missing from a partial catalog still falls back to '—' instead of an empty cell.
const buildRowsFromFeatures = (
  plans: ExplorePlanOffer[],
  tierKeys: NewTierPlan['key'][],
): ComparePlansTableRow[] | null => {
  const referencePlan = plans.find((plan) => plan.features.length > 0);
  if (!referencePlan) return null;

  const featuresByPlanKey = new Map(
    plans.map((plan) => [plan.key, new Map(plan.features.map((feature) => [feature.code, feature]))]),
  );

  return [...referencePlan.features]
    .sort((a, b) => a.display_order - b.display_order)
    .map((referenceFeature) => ({
      attribute: referenceFeature.label,
      values: tierKeys.reduce((values, key) => {
        const feature = featuresByPlanKey.get(key)?.get(referenceFeature.code);
        values[key] = feature ? formatFeatureValue(feature) : '—';
        return values;
      }, {} as Record<NewTierPlan['key'], string>),
    }));
};

export type ComparePlansTable = {
  isLoading: boolean;
  columns: ComparePlansColumn[];
  rows: ComparePlansTableRow[];
  error: PricingCatalogErrorCode | null;
  refetch: () => void;
  // Same offers ExplorePlansModal forwards to useHandleSelectNewTierPlan, so
  // checkout from the 6c table resolves gateway_id/coupon precedence instead of the hardcoded plan.
  offers: ExplorePlanOffer[];
};

// getNewTierPlans() is the always-3-tier skeleton for the 6c
// columns ("Comparar todos os planos" promises one column per tier, unlike the 6a grid which
// sizes itself to however many offers the backend returns). usePlansCatalog().plans overlays
// price/isCurrent/isUpgradeable per tier when the backend has an offer for it — a tier without
// one (customer already above it) still gets a column, just without an upgrade CTA.
export const useComparePlansTable = (): ComparePlansTable => {
  const { plans, isLoading, error, refetch } = usePlansCatalog();

  return useMemo(() => {
    const catalogByKey = new Map(plans.map((plan) => [plan.key, plan]));
    const tierSkeleton = getNewTierPlans();

    const columns = tierSkeleton.map((fallbackPlan) => {
      const catalogPlan = catalogByKey.get(fallbackPlan.key);
      const isCurrent = Boolean(catalogPlan?.isCurrent);
      const isUpgradeable = Boolean(
        catalogPlan && !catalogPlan.isCurrent && !catalogPlan.isDowngrade,
      );

      return {
        key: fallbackPlan.key,
        title: catalogPlan?.title ?? fallbackPlan.title,
        price: catalogPlan?.price ?? fallbackPlan.price,
        pill: getComparePlanPill(fallbackPlan.key, isCurrent),
        isCurrent,
        isUpgradeable,
      };
    });

    const tierKeys = tierSkeleton.map((plan) => plan.key);
    const rows = buildRowsFromFeatures(plans, tierKeys) ?? COMPARE_PLANS_TABLE_ROWS;

    return { isLoading, columns, rows, error, refetch, offers: plans };
  }, [plans, isLoading, error, refetch]);
};
